import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Reminder, RecurringType } from '@shared/types';
import { apiFetch } from '@/lib/api';
import { useApiToken } from './useApiToken';

const KEY = ['reminders'] as const;

export function useReminders() {
  const { data: tokenData, isSuccess: tokenReady } = useApiToken();
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<Reminder[]>('/reminders', { token: tokenData?.token }),
    enabled: tokenReady,
    // Reminders tick on a 60s backend dispatcher — refetching at the
    // same cadence keeps the header's "next reminder" line close to
    // realtime without spamming.
    refetchInterval: 60_000,
  });
}

export interface CreateReminderInput {
  message: string;
  remindAt: string;
  recurringType?: RecurringType | null;
  recurringDays?: number[] | null;
}

export function useCreateReminder() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: (input: CreateReminderInput) =>
      apiFetch<Reminder>('/reminders', {
        method: 'POST',
        body: input,
        token: tokenData?.token,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface UpdateReminderInput {
  message?: string;
  remindAt?: string;
  recurringType?: RecurringType | null;
  recurringDays?: number[] | null;
}

export function useUpdateReminder() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateReminderInput & { id: string }) =>
      apiFetch<Reminder>(`/reminders/${id}`, {
        method: 'PATCH',
        body: patch,
        token: tokenData?.token,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteReminder() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/reminders/${id}`, {
        method: 'DELETE',
        token: tokenData?.token,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
